import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Header } from '../components/layout/Header';
import { KPICard } from '../components/dashboard/KPICard';
import { CostChart } from '../components/dashboard/CostChart';
import { ProviderBreakdown } from '../components/dashboard/ProviderBreakdown';
import { getCostsSummary } from '../lib/api';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar, CalendarDays, Cloud } from 'lucide-react';
import { toast } from 'sonner';

const PROVIDER_STYLE = {
  aws: { color: 'bg-[#FF9900]/10', iconColor: '#FF9900' },
  azure: { color: 'bg-[#007FFF]/10', iconColor: '#007FFF' },
  gcp: { color: 'bg-[#4285F4]/10', iconColor: '#4285F4' },
  ovh: { color: 'bg-[#123F6D]/10', iconColor: '#123F6D' }
};

const computeCosts = (providers) => {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonth = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
  const currentYear = `${now.getFullYear()}`;
  const prevYear = `${now.getFullYear() - 1}`;
  const todayStr = now.toISOString().slice(0, 10);
  // Same day-of-year cutoff for previous year (fair comparison)
  const prevYearCutoff = `${now.getFullYear() - 1}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const monthName = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  let totalMonth = 0;
  let totalPrevMonth = 0;
  let totalYear = 0;
  let totalPrevYear = 0;
  const perProvider = {};
  const providerDaysWithData = {};
  const totalDaysWithData = new Set();

  (providers || []).forEach(p => {
    let provMonth = 0;
    let provPrevMonth = 0;
    const provDays = new Set();
    (p.cost_by_day || []).forEach(d => {
      if (d.date.startsWith(currentMonth)) {
        totalMonth += d.cost;
        provMonth += d.cost;
        provDays.add(d.date);
        totalDaysWithData.add(d.date);
      }
      if (d.date.startsWith(prevMonth)) {
        totalPrevMonth += d.cost;
        provPrevMonth += d.cost;
      }
      if (d.date.startsWith(currentYear) && d.date <= todayStr) {
        totalYear += d.cost;
      }
      if (d.date.startsWith(prevYear) && d.date <= prevYearCutoff) {
        totalPrevYear += d.cost;
      }
    });
    const change = provPrevMonth > 0
      ? parseFloat((((provMonth - provPrevMonth) / provPrevMonth) * 100).toFixed(1))
      : 0;
    const provDaysCount = provDays.size;
    const provForecast = provDaysCount > 0 ? (provMonth / provDaysCount) * daysInMonth : 0;
    perProvider[p.provider] = { cost: provMonth, change, forecast: provForecast };
    providerDaysWithData[p.provider] = provDaysCount;
  });

  const totalChange = totalPrevMonth > 0
    ? parseFloat((((totalMonth - totalPrevMonth) / totalPrevMonth) * 100).toFixed(1))
    : 0;

  const yearChange = totalPrevYear > 0
    ? parseFloat((((totalYear - totalPrevYear) / totalPrevYear) * 100).toFixed(1))
    : null;

  const totalDaysCount = totalDaysWithData.size;
  const forecastMonth = totalDaysCount > 0 ? (totalMonth / totalDaysCount) * daysInMonth : 0;

  return { totalMonth, totalYear, totalPrevYear, totalChange, yearChange, perProvider, monthName, year: currentYear, prevYear, forecastMonth };
};

export const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartRange, setChartRange] = useState('24m');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getCostsSummary();
        setData(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <Header title="Dashboard" subtitle="Chargement des données..." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <Skeleton className="h-96 lg:col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header
        title="Dashboard"
        subtitle={`Dernière mise à jour: ${new Date(data?.last_updated).toLocaleString('fr-FR')}`}
      />

      {/* KPI Cards */}
      {(() => {
        const costs = computeCosts(data?.providers);
        return (
          <div className="space-y-4 mb-6" data-testid="kpi-grid">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <KPICard
                title="Coût du Mois"
                subtitle={costs.monthName.charAt(0).toUpperCase() + costs.monthName.slice(1)}
                value={costs.totalMonth}
                change={costs.totalChange}
                icon={Calendar}
                color="bg-primary/10"
                forecast={costs.forecastMonth}
              />
              <KPICard
                title="Coût de l'Année"
                subtitle={costs.totalPrevYear > 0
                  ? `${costs.year} — ${costs.prevYear} : ${costs.totalPrevYear.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`
                  : costs.year
                }
                value={costs.totalYear}
                change={costs.yearChange}
                changeLabel={`vs même période ${costs.prevYear}`}
                icon={CalendarDays}
                color="bg-primary/10"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(data?.providers || [])
                .slice()
                .sort((a, b) => (costs.perProvider[b.provider]?.cost || 0) - (costs.perProvider[a.provider]?.cost || 0))
                .map(provider => (
                <KPICard
                  key={provider.provider}
                  title={provider.provider.toUpperCase()}
                  subtitle={costs.monthName.charAt(0).toUpperCase() + costs.monthName.slice(1)}
                  value={costs.perProvider[provider.provider]?.cost || 0}
                  change={costs.perProvider[provider.provider]?.change || 0}
                  iconElement={<Cloud className="w-6 h-6" style={{ color: PROVIDER_STYLE[provider.provider]?.iconColor }} />}
                  color={PROVIDER_STYLE[provider.provider]?.color}
                  forecast={costs.perProvider[provider.provider]?.forecast}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 items-stretch gap-6 mb-6">
        <div className="lg:col-span-2">
          <CostChart data={data} range={chartRange} onRangeChange={setChartRange} />
        </div>
        <ProviderBreakdown data={data} range={chartRange} />
      </div>
    </Layout>
  );
};
